import {Editor, Mark, mergeAttributes} from "@tiptap/core";
import Blockquote from "@tiptap/extension-blockquote";
import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import CharacterCount from "@tiptap/extension-character-count";
import Document from "@tiptap/extension-document";
import History from "@tiptap/extension-history";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Text from "@tiptap/extension-text";
import Underline from "@tiptap/extension-underline";
import Utils from "../utils/index.js";

export const RTE_WRAPPER_CLASS = "rich-text-editor-wrapper";
export const RTE_TOOLBAR_CLASS = "rich-text-editor-toolbar";

export const RTE_WRAPPER_SELECTOR = `.${RTE_WRAPPER_CLASS}`;
export const RTE_TOOLBAR_SELECTOR = `.${RTE_TOOLBAR_CLASS}`;

const RTE_WRAPPER_EMPTY_CLASS = "is-empty";

// Optional extensions
export const EXTENSIONS = {
	BLOCKQUOTE:  "blockquote",
	BOLD:        "bold",
	BULLETLIST:  "bulletList",
	ITALIC:      "italic",
	LINK:        "link",
	ORDEREDLIST: "orderedList",
	SMALLCAPS:   "smallCaps",
	SUBSCRIPT:   "subscript",
	SUPERSCRIPT: "superscript",
	UNDERLINE:   "underline"
};

// Things that have buttons for them in the toolbar
export const nodesAndMarks = [
	EXTENSIONS.BLOCKQUOTE,
	EXTENSIONS.BOLD,
	EXTENSIONS.BULLETLIST,
	EXTENSIONS.ITALIC,
	EXTENSIONS.LINK,
	EXTENSIONS.ORDEREDLIST,
	EXTENSIONS.SMALLCAPS,
	EXTENSIONS.SUBSCRIPT,
	EXTENSIONS.SUPERSCRIPT,
	EXTENSIONS.UNDERLINE
];

const DUMMY_EDITOR_CLASS = "dummy-editor";
const DUMMY_EDITOR_SELECTOR = `.${DUMMY_EDITOR_CLASS}`;

class RichTextEditor {
	constructor(data = {}, callbacks = {}) {
		this.getData(data);
		this.getCallbacks(callbacks);

		// Other variables
		(this as any).blurKey = null;
		(this as any).isLastEnterModified = false;
		(this as any).isSettingLink = false;
		(this as any).isEmojiPickerSetup = false;
		(this as any).enabledNodesAndMarks = (this as any).enabledExtensions.length > 0
			? nodesAndMarks.filter(value => (this as any).enabledExtensions.includes(value))
			: nodesAndMarks;
		(this as any).wrapper = (this as any).element.closest(RTE_WRAPPER_SELECTOR);
		(this as any).toolbar = (this as any).wrapper.querySelector(RTE_TOOLBAR_SELECTOR);

		// Set up editor
		if ((this as any).constructEditorImmediately || (this as any).autofocus)
			this.constructRichTextEditor();
		else
			this.setupDummyEditor();

		// Set the right classes on the toolbar for editor styles to work properly
		if ((this as any).showToolbarOnFocusOnly || !(this as any).editable)
			this.hideToolbar();
	}

	// @ts-expect-error TS(7006) FIXME: Parameter 'data' implicitly has an 'any' type.
	getData = (data) => {
		const {
			blurOnEnter = false,
			content = "",
			editable = true,
			placeholder = "",
			showToolbarOnFocusOnly = false,

			// Immediately focus editor upon construction.
			autofocus = false,

			// If false, the tiptap editor will not be constructed immediately,
			// and an interactable div will be the placeholder element until
			// content needs to be edited by the user. This should cut performance
			// costs if there are many editors on the page at the same time.
			constructEditorImmediately = true,

			// A list of extensions to be enabled in the editor instance. If not
			// provided, all installed extensions will be enabled.
			enabledExtensions = [],

			// The HTML element to attach the rich text editor to.
			element,

			// Maximum number of characters allowed. Default (0) is unlimited.
			limit = 0,

			// See https://prosemirror.net/docs/ref/#model.ParseOptions.preserveWhitespace
			// Unsure if https://prosemirror.net/docs/ref/#model.NodeSpec.whitespace
			// would be better, but preserveWhitespace seems to work for our purposes
			preserveWhitespaceFull = true
		} = data;

		(this as any).autofocus = autofocus;
		(this as any).blurOnEnter = blurOnEnter;
		(this as any).constructEditorImmediately = constructEditorImmediately;
		(this as any).content = content;
		(this as any).editable = editable;
		(this as any).enabledExtensions = enabledExtensions;
		(this as any).element = element;
		(this as any).limit = limit;
		(this as any).placeholder = placeholder;
		(this as any).preserveWhitespaceFull = preserveWhitespaceFull;
		(this as any).showToolbarOnFocusOnly = showToolbarOnFocusOnly;
	};

	// @ts-expect-error TS(7006) FIXME: Parameter 'callbacks' implicitly has an 'any' type... Remove this comment to see the full error message
	getCallbacks = (callbacks) => {
		const {
			onBlur,
			onFocus,
			onKeyDownEnter,
			onSetUpToolbar,
			onUpdate
		} = callbacks;

		(this as any).onBlur = onBlur;
		(this as any).onFocus = onFocus;
		(this as any).onKeyDownEnter = onKeyDownEnter;
		(this as any).onSetUpToolbar = onSetUpToolbar;
		(this as any).onUpdate = onUpdate;
	};

	setupDummyEditor() {
		// Reset variables
		(this as any).editor = null;
		(this as any).isLastEnterModified = false;

		// Create div
		const div = document.createElement("div");
		div.classList.add(DUMMY_EDITOR_CLASS);

		// Content
		this._setDummyPlaceholder(div);

		// Event handling
		if ((this as any).editable) {
			// @ts-expect-error TS(2322) FIXME: Type 'string' is not assignable to type 'number'.
			div.tabIndex = "0";
			div.addEventListener("click", this.replaceDummyEditor);
			div.addEventListener("focus", this.replaceDummyEditor);
		} else
			div.classList.add("disabled");

		// Add div to DOM
		(this as any).element.append(div);

		this.adjustIsEmptyClass();
	}

	// @ts-expect-error TS(7006) FIXME: Parameter 'div' implicitly has an 'any' type.
	_setDummyPlaceholder(div) {
		// clear any existing child nodes (like a leftover <p> when called from clearEditorContent())
		div.textContent = "";

		if ((this as any).placeholder !== "" && (!(this as any).content || (this as any).content === "" || (this as any).content === "<p></p>")) {
			// Is empty and has a placeholder, add placeholder (emulating tiptap placeholder)
			const p = document.createElement("p");
			p.classList.add("is-editor-empty");
			p.setAttribute("data-placeholder", (this as any).placeholder);
			div.append(p);
		} else {
			// Has no placeholder, or has content
			div.innerHTML = (this as any).content;
		}
	}

	// When dummy editor is interacted with, replace it with the real one.
	replaceDummyEditor = () => {
		const div = (this as any).wrapper.querySelector(DUMMY_EDITOR_SELECTOR);
		div.remove();
		this.constructRichTextEditor();
		this.focus();
	};

	constructRichTextEditor = () => {
		// @ts-expect-error TS(2339) FIXME: Property 'autofocus' does not exist on type 'RichT... Remove this comment to see the full error message
		const {autofocus, content, editable, element} = this;

		// Create tiptap editor
		(this as any).editor = new Editor({
			autofocus,
			content,
			editable,
			element,
			editorProps: {
				handleKeyDown: this.onEditorKeyDown
			},
			extensions: this.getExtensionsToUse(),
			...((this as any).preserveWhitespaceFull && {
				parseOptions: {
					preserveWhitespace: "full"
				}
			})

		});

		this.adjustIsEmptyClass();

		// Add event handlers
		(this as any).editor.on("blur", this.onEditorBlur.bind(this));
		(this as any).editor.on("focus", this.onEditorFocus.bind(this));
		(this as any).editor.on("selectionUpdate", this.onEditorSelectionUpdate.bind(this));
		(this as any).editor.on("update", this.onEditorUpdate.bind(this));

		// Set up toolbar
		if ((this as any).toolbar.children.length === 0)
			this.setupToolbar();
	};

	/** Adds/removes a class used for styling borders **/
	adjustIsEmptyClass() {
		if (this.getEditorContentText() === "")
			(this as any).element.firstElementChild.classList.add(RTE_WRAPPER_EMPTY_CLASS);
		else
			(this as any).element.firstElementChild.classList.remove(RTE_WRAPPER_EMPTY_CLASS);
	}

	getExtensionsToUse = () => {
		const extensions = [
			// Required
			Document,
			Paragraph,
			Text,
			// Other
			CharacterCount.configure({
				limit: (this as any).limit
			}),
			History,
			ListItem,
			Placeholder.configure({
				placeholder:          (this as any).placeholder,
				showOnlyWhenEditable: false
			})
		];

		if ((this as any).enabledExtensions.length === 0) {
			// If unspecified, enable everything
			extensionsMap.forEach(value => {
				extensions.push(...value);
			});
		} else {
			// Otherwise, only enable what is wanted
			extensionsMap.forEach((value, key) => {
				if ((this as any).enabledExtensions.includes(key))
					extensions.push(...value);
			});
		}

		return extensions;
	};

	destroy() {
		if ((this as any).editor) {
			(this as any).editor.destroy();
			(this as any).editor = null;
		} else
			// Remove dummy div
			(this as any).wrapper.firstElementChild.remove();
	}

	/****************************************************************
	 ******************** Editor Event Handlers *********************
	 ****************************************************************/
	// @ts-expect-error TS(7031) FIXME: Binding element 'editor' implicitly has an 'any' t... Remove this comment to see the full error message
	onEditorBlur = ({editor, event}) => {
		// Clicked on toolbar
		const relatedTarget = event.relatedTarget;
		if (relatedTarget?.closest(RTE_WRAPPER_SELECTOR) || (this as any).isSettingLink) {
			(this as any).isSettingLink = false;
			// Clear blur key info
			(this as any).blurKey = null;
			return;
		}

		if ((this as any).showToolbarOnFocusOnly)
			this.hideToolbar();

		if (!(this as any).constructEditorImmediately) {
			// Destroy RTE and replace with the dummy editor
			(this as any).content = editor.getHTML();
			this.destroy();
			this.setupDummyEditor();
		}

		if ((this as any).onBlur) {
			const isKeyBlur = (this as any).blurKey !== null;
			(this as any).onBlur({
				editor,
				event,
				details: {
					type: isKeyBlur ? "keyboard" : undefined,
					key:  isKeyBlur ? (this as any).blurKey : undefined
				}
			});
		}

		// Clear blur key info
		(this as any).blurKey = null;
	};

	// @ts-expect-error TS(7031) FIXME: Binding element 'editor' implicitly has an 'any' t... Remove this comment to see the full error message
	onEditorFocus = ({editor, event}) => {
		if ((this as any).showToolbarOnFocusOnly && (this as any).editable)
			this.showToolbar();

		// @ts-expect-error TS(2551) FIXME: Property 'onFocus' does not exist on type 'RichTex... Remove this comment to see the full error message
		if (this.onFocus)
		// @ts-expect-error TS(2551) FIXME: Property 'onFocus' does not exist on type 'RichTex... Remove this comment to see the full error message
			this.onFocus({editor, event});
	};

	// @ts-expect-error TS(7006) FIXME: Parameter 'view' implicitly has an 'any' type.
	onEditorKeyDown = (view, event) => {
		const key = event.key;

		// For some reason, the blur command causes an error in Chrome when used
		// in this function, but not in Firefox. So just.. silence it. Nothing
		// seems to break.
		const blur = () => {
			try {
				(this as any).editor.commands.blur();
			} catch (e) { }
		};

		if (key === "Enter") {
			if (event.altKey || event.shiftKey || event.ctrlKey || event.metaKey) {
				// This command chain causes another keydown event for "Enter",
				// so the this.isLastEnterModified flag is used to check if it
				// came from here
				(this as any).isLastEnterModified = true;
				(this as any).editor.chain().enter().run();
				return true;
			} else if ((this as any).isLastEnterModified) {
				(this as any).isLastEnterModified = false;
				return false;
			} else {
				if ((this as any).onKeyDownEnter)
					(this as any).onKeyDownEnter((this as any).editor);

				if ((this as any).blurOnEnter) {
					(this as any).blurKey = "Enter";
					blur();
				}

				return true;
			}
		} else if ((this as any).blurOnEnter && (key === "Escape" || key === "Esc")) {
			if ((this as any).onKeyDownEsc)
				(this as any).onKeyDownEsc((this as any).editor);

			(this as any).blurKey = "Escape";
			blur();
			return true;
		}

		return false;
	};

	// @ts-expect-error TS(7031) FIXME: Binding element 'editor' implicitly has an 'any' t... Remove this comment to see the full error message
	onEditorSelectionUpdate = ({editor}) => {
		this.updateEditorToolbar({editor});
	};

	// @ts-expect-error TS(7031) FIXME: Binding element 'editor' implicitly has an 'any' t... Remove this comment to see the full error message
	onEditorUpdate = ({editor}) => {
		this.updateEditorToolbar({editor});

		if ((this as any).onUpdate)
			(this as any).onUpdate({editor});
	};

	/****************************************************************
	 *********************** Editor Actions *************************
	 ****************************************************************/
	clearEditorContent = () => {
		(this as any).content = "";
		if ((this as any).editor)
			(this as any).editor.commands.clearContent();
		else {
			const div = (this as any).wrapper.querySelector(DUMMY_EDITOR_SELECTOR);
			this._setDummyPlaceholder(div);
		}
	};

	getEditorContentText() {
		if ((this as any).editor)
			return (this as any).editor.getText();
		else
			return (this as any).wrapper.querySelector(DUMMY_EDITOR_SELECTOR).textContent;
	}

	getEditorContentHTML() {
		if ((this as any).editor)
			return (this as any).editor.getHTML();
		else
			return (this as any).wrapper.querySelector(DUMMY_EDITOR_SELECTOR).innerHTML;
	}

	// @ts-expect-error TS(7006) FIXME: Parameter 'content' implicitly has an 'any' type.
	setEditorContent(content) {
		if ((this as any).editor) {
			const options = (this as any).preserveWhitespaceFull
				? {preserveWhitespace: "full"}
				: undefined;

			(this as any).editor.commands.setContent(content, options);
		} else {
			(this as any).content = content;

			// Set the content, or the placeholder if there is no content
			this._setDummyPlaceholder((this as any).element.firstElementChild);
		}

		this.adjustIsEmptyClass();
	}

	/**
	 * Makes the editor editable or not editable, and shows/hides the toolbar
	 * accordingly. If the editor is made editable, focus will be moved inside
	 * the editor to the end of the content.
	 *
	 * @param {boolean} editable
	 */
	// @ts-expect-error TS(7006) FIXME: Parameter 'editable' implicitly has an 'any' type.
	setEditable(editable) {
		(this as any).editable = editable;

		if ((this as any).editor) {
			(this as any).editor.options.editable = editable;
			(this as any).editor.view.update((this as any).editor.view.props);
		} else
			this.replaceDummyEditor();

		if (!(this as any).editable)
			this.hideToolbar();
	}

	focus() {
		if ((this as any).editor)
		// Always position cursor at the end of the editor
			(this as any).editor.commands.focus("end");
		else
			this.replaceDummyEditor();
	}

	/****************************************************************
	 *********************** Toolbar Related ************************
	 ****************************************************************/
	setupToolbar = () => {
		const buttonsMap = new Map([
			[EXTENSIONS.BOLD, {
				label:   "&#119809;",
				onClick: () => this.onClickToolbarBold({editor: (this as any).editor}),
				tooltip: "Bold"
			}],
			[EXTENSIONS.ITALIC, {
				label:   "&#119920;",
				onClick: () => this.onClickToolbarItalic({editor: (this as any).editor}),
				tooltip: "Italic"
			}],
			[EXTENSIONS.UNDERLINE, {
				label:   "&#9089;",
				onClick: () => this.onClickToolbarUnderline({editor: (this as any).editor}),
				tooltip: "Underline"
			}],
			[EXTENSIONS.SUPERSCRIPT, {
				label:   "A&#7491;",
				onClick: () => this.onClickToolbarSuperscript({editor: (this as any).editor}),
				tooltip: "Superscript"
			}],
			[EXTENSIONS.SUBSCRIPT, {
				label:   "A&#8336;",
				onClick: () => this.onClickToolbarSubscript({editor: (this as any).editor}),
				tooltip: "Subscript"
			}],
			[EXTENSIONS.SMALLCAPS, {
				label:   "A&#7424;",
				onClick: () => this.onClickToolbarSmallCaps({editor: (this as any).editor}),
				tooltip: "Small caps"
			}],
			[EXTENSIONS.BULLETLIST, {
				label:   "&#8226;",
				onClick: () => this.onClickToolbarBulletList({editor: (this as any).editor}),
				tooltip: "Bullet list"
			}],
			[EXTENSIONS.ORDEREDLIST, {
				label:   "1..",
				onClick: () => this.onClickToolbarOrderedList({editor: (this as any).editor}),
				tooltip: "Numbered list"
			}],
			[EXTENSIONS.BLOCKQUOTE, {
				label:   "&#10078;",
				onClick: () => this.onClickToolbarBlockquote({editor: (this as any).editor}),
				tooltip: "Blockquote"
			}],
			[EXTENSIONS.LINK, {
				label:   "Link",
				onClick: () => this.onClickToolbarLink({editor: (this as any).editor}),
				tooltip: "Link"
			}]
		]);

		// The last button in a "group" will have a class added for a visual separator
		const lastButtonInGroup = [
			EXTENSIONS.UNDERLINE,
			EXTENSIONS.SMALLCAPS
		];

		// Names of enabled toolbar button functions
		const buttons = [];
		buttonsMap.forEach((value, key) => {
			// Skip disabled ones
			if (!(this as any).enabledNodesAndMarks.includes(key))
				return;

			const button = document.createElement("button");
			button.classList.add(`editor-toolbar-btn-${key}`);
			button.innerHTML = value.label;
			button.setAttribute("data-tooltip-text", value.tooltip);
			button.setAttribute("aria-label", value.tooltip);
			button.addEventListener("click", value.onClick);

			if (lastButtonInGroup.includes(key))
				button.classList.add("border-right");

			buttons.push(button);
		});

		// Emoji button
		if (!(this as any).disableEmojiBtn) {
			const emojiBtn = document.createElement("button");
			emojiBtn.classList.add("emoji-picker");
			emojiBtn.innerText = "=)";
			emojiBtn.setAttribute("data-tooltip-text", "Emoji");
			emojiBtn.setAttribute("aria-label", "Emoji");
			emojiBtn.addEventListener("click", (event) => {
				if (!(this as any).isEmojiPickerSetup)
					this.setUpEmojiButton();

				this.onClickEmojiBtn(event);
			});
			buttons.push(emojiBtn);
		}

		// Keyboard blur
		buttons.forEach((button) => {
			button.addEventListener("blur", (event) => {
				if (!(this as any).wrapper.contains(event.relatedTarget))
					this.onEditorBlur({editor: (this as any).editor, event});
			});
		});

		(this as any).toolbar.append(...buttons);

		// @ts-expect-error TS(2551) FIXME: Property 'onSetUpToolbar' does not exist on type '... Remove this comment to see the full error message
		if (this.onSetUpToolbar)
		// @ts-expect-error TS(2551) FIXME: Property 'onSetUpToolbar' does not exist on type '... Remove this comment to see the full error message
			this.onSetUpToolbar();
	};

	setUpEmojiButton() {
		const emojiButton = (this as any).toolbar.querySelector(".emoji-picker");

		if ((this as any).disableEmojiBtn)
			emojiButton.classList.add("hidden");
		else if (!(this as any).isEmojiPickerSetup) {
			// Add emoji picker to DOM here instead of in the JSX to avoid errors
			const emojiPicker = document.createElement("emoji-picker");
			emojiPicker.classList.add("hidden");

			emojiPicker.addEventListener("emoji-click", event => {
				(this as any).editor.chain().focus().insertContent((event as any).detail.unicode).run();
			});

			// Close emoji picker when there's a click outside of it or its button
			document.addEventListener("click", event => {
				// Get the top level emoji picker element in its shadow DOM
				const pickerRootElem = emojiPicker.shadowRoot?.querySelector(".picker");
				const target = event.composedPath()[0] as Node; // event.target won't work if it's inside the shadow dom

				// Don't close the picker if it's a click inside of it
				if (pickerRootElem?.contains(target))
					return;

				if (emojiButton && emojiPicker && !emojiPicker.isSameNode(target)
					&& !emojiButton.isSameNode(target)) {
					emojiButton.classList.remove("selected");
					emojiPicker.classList.add("hidden");
				}
			});

			// Add scroll bar styles
			const style = document.createElement("style");
			style.textContent = `
				.tabpanel {
					scrollbar-width: thin;
					scrollbar-color: #c1c1c1 #fbfbfb;
				}

				.tabpanel::-webkit-scrollbar {
					width: 10px;
					-webkit-appearance: none;
					background: #fbfbfb;
				}
				.tabpanel::-webkit-scrollbar-thumb {
					-webkit-box-shadow: inset 1px 0 0 #fbfbfb;
					-webkit-border-radius: 20px;
					background-clip: content-box;
					background-color: #c1c1c1;
				}
				.tabpanel::-webkit-scrollbar-track {
					-webkit-border-radius: 0;
					border-left: 1px solid #e8e8e8;
					background: #fbfbfb;
				}
			`;
			// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
			emojiPicker.shadowRoot.append(style);

			emojiButton.parentElement.append(emojiPicker);
			(this as any).isEmojiPickerSetup = true;
		} else
			emojiButton.classList.remove("hidden");
	}

	// @ts-expect-error TS(7031) FIXME: Binding element 'editor' implicitly has an 'any' t... Remove this comment to see the full error message
	updateEditorToolbar = ({editor}) => {
		// Figure out what styles are being used in the selection and update
		// buttons highlighted accordingly
		// @ts-expect-error TS(7006) FIXME: Parameter 'nodeOrMark' implicitly has an 'any' typ... Remove this comment to see the full error message
		(this as any).enabledNodesAndMarks.forEach(nodeOrMark => {
			// @ts-expect-error TS(2339) FIXME: Property 'toolbar' does not exist on type 'RichTex... Remove this comment to see the full error message
			const button = this.toolbar.querySelector(`.editor-toolbar-btn-${nodeOrMark}`);
			let isNodeOrMarkActive = false;
			const extensions = extensionsMap.get(nodeOrMark);
			// @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
			for (const extension of extensions) {
				isNodeOrMarkActive = editor.isActive(extension.name);
				if (isNodeOrMarkActive)
					break;
			}
			// Add styling if active
			if (isNodeOrMarkActive)
				button.classList.add("selected");
			else
				button.classList.remove("selected");
		});
	};

	hideToolbar = () => {
		(this as any).wrapper.classList.add("hide-toolbar");
	};

	showToolbar = () => {
		(this as any).wrapper.classList.remove("hide-toolbar");
	};

	/****************************************************************
	 *********************** Toolbar Buttons ************************
	 ****************************************************************/
	// @ts-expect-error TS(2304) FIXME: Cannot find name 'onClickToolbarBlockquote'.
	onClickToolbarBlockquote = ({editor}) => {
		editor.chain().toggleBlockquote().focus().run();
	};

	// @ts-expect-error TS(2304) FIXME: Cannot find name 'onClickToolbarBold'.
	onClickToolbarBold = ({editor}) => {
	// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
		if (document.getSelection().focusNode.parentElement.closest("strong"))
			editor.chain().toggleBold_strong().focus().run();
		else
			editor.chain().toggleBold().focus().run();
	};

	// @ts-expect-error TS(2304) FIXME: Cannot find name 'onClickToolbarBulletList'.
	onClickToolbarBulletList = ({editor}) => {
		editor.chain().toggleBulletList().focus().run();
	};

	// @ts-expect-error TS(2304) FIXME: Cannot find name 'onClickToolbarItalic'.
	onClickToolbarItalic = ({editor}) => {
	// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
		if (document.getSelection().focusNode.parentElement.closest("em"))
			editor.chain().toggleItalic_em().focus().run();
		else
			editor.chain().toggleItalic().focus().run();
	};

	// @ts-expect-error TS(2304) FIXME: Cannot find name 'onClickToolbarLink'.
	onClickToolbarLink = ({editor}) => {
		// If the text selection is a link, uses the url set for it in the prompt
		let defaultValue = editor.getAttributes("link").href;
		// Otherwise, if the selected text is a valid url, it will use that
		if (!defaultValue) {
			const selectedText = window.getSelection()?.toString();
			if (selectedText && Utils.Common.isUrlValid(selectedText))
				defaultValue = selectedText;
		}

		if (!defaultValue)
			defaultValue = "https://";

		(this as any).isSettingLink = true;

		let enteredUrl = window.prompt("URL", defaultValue);

		// User clicked cancel
		if (enteredUrl === null)
			return;

		// Only accept absolute URLs
		enteredUrl = enteredUrl.trim();
		if (enteredUrl === "") {
			// User clicked enter with empty field, remove link
			editor?.chain().extendMarkRange("link").unsetLink().focus().run();
		} else {
			try {
				// eslint-disable-next-line no-new
				new URL(enteredUrl);
			} catch (e) {
				window.alert("Invalid URL: " + enteredUrl);
				enteredUrl = "";
			}

			editor?.chain().extendMarkRange("link").setLink({href: enteredUrl}).focus().run();
		}
	};

	// @ts-expect-error TS(2304) FIXME: Cannot find name 'onClickToolbarOrderedList'.
	onClickToolbarOrderedList = ({editor}) => {
		editor.chain().toggleOrderedList().focus().run();
	};

	// @ts-expect-error TS(2304) FIXME: Cannot find name 'onClickToolbarSmallCaps'.
	onClickToolbarSmallCaps = ({editor}) => {
		editor.chain().toggleSmallCaps().focus().run();
	};

	// @ts-expect-error TS(2304) FIXME: Cannot find name 'onClickToolbarSubscript'.
	onClickToolbarSubscript = ({editor}) => {
		editor.chain().toggleSubscript().focus().run();
	};

	// @ts-expect-error TS(2304) FIXME: Cannot find name 'onClickToolbarSuperscript'.
	onClickToolbarSuperscript = ({editor}) => {
		editor.chain().toggleSuperscript().focus().run();
	};

	// @ts-expect-error TS(2304) FIXME: Cannot find name 'onClickToolbarUnderline'.
	onClickToolbarUnderline = ({editor}) => {
		editor.chain().toggleUnderline().focus().run();
	};

	// Open/close emoji picker
	// @ts-expect-error TS(2304) FIXME: Cannot find name 'onClickEmojiBtn'.
	onClickEmojiBtn = (event) => {
		const emojiButton = event.target;

		if (emojiButton.classList.contains("selected")) {
			emojiButton.classList.remove("selected");
			emojiButton.nextSibling.classList.add("hidden");
		} else {
			emojiButton.classList.add("selected");
			emojiButton.nextSibling.classList.remove("hidden");
		}
	};
};

export default RichTextEditor;

/** Custom extensions **/
// Preserve <b> tags for bold
const CustomBold_b = Bold.extend({
	name: "bold",
	parseHTML() {
		return [
			{
				tag:      "b",
				getAttrs: node => (node as any).style.fontWeight !== "normal" && null
			}, {
				style:    "font-weight",
				getAttrs: value => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null
			}
		];
	},
	renderHTML({HTMLAttributes}) {
		return ["b", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
	}
});

// And also preserve <strong> tags for bold
const CustomBold_strong = Bold.extend({
	name: "bold_strong",
	addCommands() {
		return {
			setBold_strong: () => ({commands}) => {
				return commands.setMark("bold_strong");
			},
			toggleBold_strong: () => ({commands}) => {
				return commands.toggleMark("bold_strong");
			},
			unsetBold_strong: () => ({commands}) => {
				return commands.unsetMark("bold_strong");
			}
		};
	},
	parseHTML() {
		return [{
			tag: "strong"
		}];
	}
});

// Preserve <i> tags for italics
const CustomItalics_i = Italic.extend({
	name: "italic",
	parseHTML() {
		return [
			{
				tag:      "i",
				getAttrs: node => (node as any).style.fontStyle !== "normal" && null
			},
			{
				style: "font-style=italic"
			}
		];
	},
	renderHTML({HTMLAttributes}) {
		return ["i", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
	}
});

// And also preserve <em> tags for italics
const CustomItalics_em = Italic.extend({
	name: "italic_em",
	addCommands() {
		return {
			setItalic_em: () => ({commands}) => {
				return commands.setMark("italic_em");
			},
			toggleItalic_em: () => ({commands
			}) => {
				return commands.toggleMark("italic_em");
			},
			unsetItalic_em: () => ({commands}) => {
				return commands.unsetMark("italic_em");
			}
		};
	},
	parseHTML() {
		return [{
			tag: "em"
		}];
	}
});

// Tiptap extension for small caps
const SmallCaps = Mark.create({
	name:       "smallCaps",
	addOptions: {
		// @ts-expect-error FIXME
		HTMLAttributes: {
			style: "font-variant: small-caps"
		}
	},
	parseHTML() {
		return [
			{
				style: "font-variant=small-caps"
			}
		];
	},
	renderHTML({HTMLAttributes}) {
		return ["span", mergeAttributes((this as any).options.HTMLAttributes, HTMLAttributes), 0];
	},
	addCommands() {
		return {
			setSmallCaps: () => ({commands}) => {
				return commands.setMark("smallCaps");
			},
			toggleSmallCaps: () => ({commands}) => {
				return commands.toggleMark("smallCaps");
			},
			unsetSmallCaps: () => ({commands}) => {
				return commands.unsetMark("smallCaps");
			}
		};
	}
});

/** Mapping for optional extensions **/
export const extensionsMap = new Map([
	[EXTENSIONS.BLOCKQUOTE, [Blockquote]],
	[EXTENSIONS.BOLD, [CustomBold_b, CustomBold_strong]],
	[EXTENSIONS.BULLETLIST, [BulletList]],
	[EXTENSIONS.ITALIC, [CustomItalics_em, CustomItalics_i]],
	[EXTENSIONS.LINK, [Link]],
	[EXTENSIONS.ORDEREDLIST, [OrderedList]],
	[EXTENSIONS.SMALLCAPS, [SmallCaps]],
	[EXTENSIONS.SUBSCRIPT, [Subscript]],
	[EXTENSIONS.SUPERSCRIPT, [Superscript]],
	[EXTENSIONS.UNDERLINE, [Underline]]
]);
